import { Component, EventEmitter, HostListener, Injector, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Account, AccountParams } from '@app/interfaces/sys/account';
import { AuditLogParams } from '@app/interfaces/sys/audit-log';
import { Auth } from '@app/interfaces/sys/auth';
import { SelectOption } from '@app/interfaces/sys/dict';
import { STChange, STColumn, STData, STPage, STScroll } from '@app/interfaces/sys/st';
import { AccountService } from '@services/sys/account.service';
import { RoleService } from '@services/sys/role.service';
import { SettingsService } from '@services/sys/settings.service';
import { TokenService } from '@services/sys/token.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
    selector: ' app-account-list',
    templateUrl: './account-list.component.html',
})
export class AccountListComponent implements OnInit {
    @Input() data: Account[] = [];
    @Input() loading = false;
    @Input() searchKeyword = '';
    @Input() page: STPage = {
        show: false,
    };
    @Input() teamId?: number;
    @Output() filterChange = new EventEmitter();
    @Output() init = new EventEmitter();

    roleList: SelectOption[] = [];
    sort = '';
    sortItems: string[] = [];
    nzScroll: STScroll = {};

    accountColumns: STColumn[] = [];
    columns: STColumn[] = [
        {
            title: 'Avatar',
            render: 'avatar',
            width: '90px',
        },
        {
            title: 'Username',
            index: 'username',
            highlight: true,
            width: '100px',
            sort: true
        },
        {
            title: 'First Name',
            index: 'name',
            highlight: true,
            width: '120px',
            sort: true
        },
        {
            title: 'Last Name',
            index: 'lastName',
            highlight: true,
            width: '100px',
            sort: true
        },
        {
            title: 'Role',
            index: 'roleNames',
            format: (data: STData) => {
                return data?.roleNames?.join(',');
            },
            highlight: true,
            width: '100px',
            nzFilterMultiple: false,
            nzFilterFn: true,
            filterIndex: 'roleId'
        },
        {
            title: 'Email',
            index: 'email',
            highlight: true,
            width: '150px',
            sort: true
        },
        {
            title: 'Phone',
            index: 'phone',
            highlight: true,
            width: '150px',
        },
        {
            title: 'Status',
            index: 'status',
            render: 'status',
            width: '80px',
            listOfFilter: [
                {
                    value: true,
                    text: 'Enable',
                },
                {
                    value: false,
                    text: 'Disable ',
                },
            ],
            nzFilterMultiple: false,
            nzFilterFn: true,
            className: 'remove-padding',
            filterIndex: 'enable'
        },
        {
            title: 'Action',
            render: 'action',
            width: '100px',
            className: 'remove-padding'
        },
    ];
    query: AccountParams = {};

    get canEnable(): boolean {
        return this.settingService.hasPermission(['Account Enable']);
    }

    get userInfo(): Auth {
        return this.tokenService.get();
    }

    constructor(
        private modal: NzModalService,
        private router: Router,
        private accountService: AccountService,
        private message: NzMessageService,
        private settingService: SettingsService,
        private tokenService: TokenService,
        private roleService: RoleService,
        private injector: Injector
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.setScroll();
        this.query = {};
        await this.fetchOptions();
        this.columns.forEach(item => {
            if (item.index === 'roleNames') {
                item.listOfFilter = this.roleList.map(option => this.formatOption(option));
            }
        });
        this.accountColumns = [...this.columns];
    }

    initData(size?: number): void {
        this.init.emit(size);
    }

    @HostListener('window:resize')
    _resize(): void {
        this.setScroll();
    }

    setScroll(): void {
        this.settingService.setLayout();
        const { y } = this.settingService.layout;
        this.nzScroll = {
            x: '1030px',
            y: `${y}px`,
        };
    }

    formatOption(option: SelectOption): {
        text: string;
        value: number | string
    } {
        return {
            text: option.label,
            value: option.value,
        };
    }

    async fetchOptions(): Promise<void> {
        this.roleList = await this.roleService.findOptions();
    }

    handleEdit(row: Account): void {
        this.router.navigate(['/accounts/detail', row.id]);
    }

    handleResetPassword(row: Account): void {
        console.log('row', row);
        this.modal.confirm({
            nzTitle: `You are resetting password for "${row.username}".`,
            nzOkType: 'primary',
            nzOkDanger: true,
            nzStyle: {top: '40vh'},
            nzOnOk: async () => {
                await this.accountService.resetPassword(row.id).then((res) => {
                    this.modal.success({
                        nzTitle: 'Reset successfully',
                        nzContent: 'The new password is: ' + res
                    });
                    if (this.userInfo.id === row.id) {
                        this.modal.error({
                            nzTitle: `Your password has been changed. Please login.`,
                            nzOnOk: () => this.injector.get(Router).navigateByUrl('/login')
                        });
                    }
                });
            },
        });
    }

    changeStatus(row: Account, enable: boolean): void {
        if (!this.canEnable || row.id === this.userInfo.id) {
            return;
        }
        this.modal.confirm({
            nzTitle: `You are ${enable ? 'activating' : 'deactivating'} "${row.username}" account.`,
            nzOkText: 'Yes',
            nzCancelText: 'No',
            nzOkType: 'primary',
            nzOkDanger: true,
            nzStyle: {top: '40vh'},
            nzOnOk: async () => {
                console.log('OK');
                const data = {
                    id: row.id,
                    enable,
                };
                await this.accountService.enable(data);
                row.enable = enable;
                this.message.success('Status updated successfully');
            },
        });
    }

    handleChange(event: STChange): void {
        if (event.type === 'filter') {
            this.query = {
                ...this.query,
                ...event.params
            };
            this.filterChange.emit(this.query);
        }
        if (event.type === 'sort') {
            this.filterChange.emit({
                sort: event.sort
            });
        }
    }
}
